import { Link } from 'react-router-dom';
import { PageFrame } from '../components/PageFrame';
import { PricingCards } from '../components/PricingCards';

export function HomePage() {
  return (
    <PageFrame>
      <section className="hero section-shell">
        <div className="hero__copy reveal">
          <p className="eyebrow">AI-assisted reflection tool</p>
          <h1>Make room for a <em>clearer thought.</em></h1>
          <p className="hero__lede">
            Sanjeom combines structured prompts with AI-assisted explanation to help you organise thoughts, explore options, and reflect before acting.
          </p>
          <div className="hero__actions">
            <Link className="button button--warm" to="/workspace">Start a conversation</Link>
            <Link className="text-link" to="/pricing">See question packs <span aria-hidden="true">↗</span></Link>
          </div>
          <div className="trust-line" aria-label="Product principles">
            <span>Calculation-based</span>
            <span>AI-assisted</span>
            <span>Reflection and entertainment</span>
          </div>
        </div>

        <div className="hero__visual reveal reveal--delay" aria-label="Example AI workspace conversation">
          <div className="orb orb--one" />
          <div className="orb orb--two" />
          <div className="workspace-card">
            <div className="workspace-card__top">
              <span className="signal-dot" />
              <span>Focused session</span>
              <span className="workspace-card__quiet">Private</span>
            </div>
            <div className="message message--user">
              I keep circling the same decision. Help me see what I am missing.
            </div>
            <div className="message message--assistant">
              <p>Let us separate the decision into three layers:</p>
              <ol>
                <li>What changes if you act now?</li>
                <li>What cost are you protecting yourself from?</li>
                <li>What evidence would make the next step reversible?</li>
              </ol>
            </div>
            <div className="composer-preview">
              <span>Continue the thought…</span>
              <span className="composer-preview__send">↑</span>
            </div>
          </div>
        </div>
      </section>

      <section className="principles section-shell">
        <div className="section-heading">
          <p className="eyebrow">A structured way to reflect</p>
          <h2>Calculation first. AI-assisted explanation.</h2>
        </div>
        <div className="principle-grid">
          <article>
            <span className="principle-number">01</span>
            <h3>Start with a clear question</h3>
            <p>Structured inputs and calculation provide a consistent foundation before any explanation is generated.</p>
          </article>
          <article>
            <span className="principle-number">02</span>
            <h3>Explore useful context</h3>
            <p>AI organises relevant considerations into accessible language, helping you examine a situation from more than one angle.</p>
          </article>
          <article>
            <span className="principle-number">03</span>
            <h3>Keep your own judgement</h3>
            <p>Use each response for personal reflection and entertainment, not as a factual prediction or professional instruction.</p>
          </article>
        </div>
      </section>

      <section className="use-cases">
        <div className="section-shell use-cases__inner">
          <div>
            <p className="eyebrow eyebrow--light">Designed for real life</p>
            <h2>For the moments that do not fit into a checklist.</h2>
          </div>
          <div className="use-case-list">
            <span>Untangle a difficult choice</span>
            <span>Prepare for a sensitive conversation</span>
            <span>Find the next move in a stalled project</span>
            <span>Reflect without being rushed</span>
          </div>
        </div>
      </section>

      <section className="pricing-section section-shell" id="pricing">
        <div className="section-heading section-heading--center">
          <p className="eyebrow">Simple question packs</p>
          <h2>Pay once. Ask when it matters.</h2>
          <p>Each submitted question receives one AI-assisted response grounded in the service's structured framework. Prices are shown in US dollars.</p>
        </div>
        <PricingCards />
        <p className="pricing-note">Payments are processed securely by Stripe. Sanjeom does not store full card details.</p>
      </section>

      <section className="closing-cta section-shell">
        <div>
          <p className="eyebrow">Begin where you are</p>
          <h2>You do not need the perfect question.</h2>
        </div>
        <Link className="button button--ink" to="/workspace">Open your workspace</Link>
      </section>
    </PageFrame>
  );
}
